import { Component, OnInit, TemplateRef } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ApiCallingService } from "../../services/api-calling.service";
import { ConfigApi } from "../../config-api";
import { ToastrService } from "ngx-toastr";
import { Globals } from "../../common/global";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { VendingOrderModalComponent } from "../vending-order-modal/vending-order-modal.component";
import * as mapboxgl from "mapbox-gl";
import { environment } from "../../../environments/environment";

@Component({
  selector: "app-vending-order-details",
  templateUrl: "./vending-order-details.component.html",
  styleUrls: ["./vending-order-details.component.css"],
})
export class VendingOrderDetailsComponent implements OnInit {
  orderDetails: any = {};
  loading: boolean = false;
  orderId: string = "";
  map!: mapboxgl.Map;

  trackingId: string = "";
  showTrackingInput: boolean = false;
  selectedRefundItemIds: string[] = [];
  refundAmount: number | null = null;
  isRefundSubmitting: boolean = false;
  private partialRefundModalRef: NgbModalRef | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiCallingService: ApiCallingService,
    private toastr: ToastrService,
    public globals: Globals,
    private modalService: NgbModal,
  ) {
    (mapboxgl as any).accessToken = environment.mapbox.accessToken;
  }

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.orderId = params["id"];
      if (this.orderId) {
        this.getOrderDetails();
      }
    });
  }
  ngAfterViewInit(): void {
    if (this.orderDetails?.userLocation?.coordinates) {
      this.loadMap();
    }
  }
  loadMap(): void {
    if (!this.orderDetails?.userLocation?.coordinates) {
      console.error("User location coordinates are not available.");
      return;
    }

    const lat = this.orderDetails.userLocation.coordinates[1];
    const lng = this.orderDetails.userLocation.coordinates[0];

    setTimeout(() => {
      if (document.getElementById("userMap")) {
        this.map = new mapboxgl.Map({
          container: "userMap",
          style: "mapbox://styles/mapbox/streets-v11",
          center: [lng, lat],
          zoom: 14,
        });

        new mapboxgl.Marker().setLngLat([lng, lat]).addTo(this.map);
      } else {
        console.error("Map container 'userMap' not found.");
      }
    }, 100);
  }

  getOrderDetails(): void {
    this.loading = true;
    this.apiCallingService
      .callAPI(
        {},
        "GET",
        `${ConfigApi.URLS.VENDING.TRACK_ORDER}/${this.orderId}`,
      )
      .subscribe(
        (res) => {
          this.loading = false;
          if (this.apiCallingService.handleSuccess(res)) {
            this.orderDetails = res.data;
            if (this.orderDetails.userLocation?.coordinates) {
              this.loadMap();
            }
            console.log("orderDetails", this.orderDetails);
          }
        },
        (error) => {
          this.loading = false;
          this.toastr.error("Failed to load order details");
          console.error("Error loading order details:", error);
        },
      );
  }
  // ngAfterViewInit(): void {
  //   if (this.orderDetails?.userLocation?.coordinates) {
  //     this.loadMap();
  //   }
  // }
  goBack(): void {
    this.router.navigate(["/user/vending-orders"]);
  }

  acceptOrder(): void {
    this.confirmAction(this.orderDetails, "accept");
  }

  outForDelivery(): void {
    this.confirmAction(this.orderDetails, "out for delivery");
  }

  completeOrder(): void {
    this.confirmAction(this.orderDetails, "complete");
  }

  readyForPickup(): void {
    this.confirmAction(this.orderDetails, "ready for pickup");
  }

  cancelOrder(): void {
    this.confirmAction(this.orderDetails, "cancel", true);
  }

  openPartialRefundModal(content: TemplateRef<any>): void {
    this.selectedRefundItemIds = [];
    this.refundAmount = null;
    this.partialRefundModalRef = this.modalService.open(content, {
      centered: true,
      backdrop: "static",
      size: "xl",
    });
  }

  closePartialRefundModal(): void {
    this.partialRefundModalRef?.close();
    this.partialRefundModalRef = null;
  }

  getRefundItemId(item: any): string {
    return (
      item?._id || item?.id || item?.packageItemId || item?.packageId?._id || ""
    );
  }

  isRefundItemSelected(item: any): boolean {
    const itemId = this.getRefundItemId(item);
    return !!itemId && this.selectedRefundItemIds.includes(itemId);
  }

  onRefundItemToggle(item: any, event: Event): void {
    const itemId = this.getRefundItemId(item);
    const isChecked = (event.target as HTMLInputElement).checked;

    if (!itemId) {
      this.toastr.warning("Unable to identify selected product item");
      return;
    }

    if (isChecked) {
      if (!this.selectedRefundItemIds.includes(itemId)) {
        this.selectedRefundItemIds = [...this.selectedRefundItemIds, itemId];
      }
      return;
    }

    this.selectedRefundItemIds = this.selectedRefundItemIds.filter(
      (selectedItemId) => selectedItemId !== itemId,
    );
  }

  submitPartialRefund(): void {
    if (!this.orderDetails?._id) {
      this.toastr.error("Order details not found");
      return;
    }

    if (!this.selectedRefundItemIds.length) {
      this.toastr.warning("Please select at least one product");
      return;
    }

    if (this.refundAmount === null || Number(this.refundAmount) <= 0) {
      this.toastr.warning("Please enter a valid refund amount");
      return;
    }

    const payload = {
      itemIds: this.selectedRefundItemIds,
      refundAmount: Number(this.refundAmount),
    };

    this.isRefundSubmitting = true;
    this.apiCallingService
      .callAPI(
        payload,
        "POST",
        `${ConfigApi.URLS.VENDING.REFUND_PACKAGE_ITEMS}/${this.orderDetails._id}`,
      )
      .subscribe(
        (res) => {
          this.isRefundSubmitting = false;
          if (this.apiCallingService.handleSuccess(res)) {
            this.toastr.success("Partial refund processed successfully");
            this.closePartialRefundModal();
            this.getOrderDetails();
          } else {
            this.toastr.error(
              res?.message || "Failed to process partial refund",
            );
          }
        },
        () => {
          this.isRefundSubmitting = false;
          this.toastr.error("Failed to process partial refund");
        },
      );
  }

  rejectOrder(): void {
    this.confirmAction(this.orderDetails, "reject", true);
  }

  confirmAction(
    order: any,
    actionType: string,
    requiresReason: boolean = false,
  ): void {
    const modalRef = this.modalService.open(VendingOrderModalComponent, {
      centered: true,
      backdrop: "static",
    });
    const title =
      actionType.toLowerCase() === "ready for pickup"
        ? "Ready for Pickup"
        : actionType.toLowerCase() === "out for delivery"
          ? "Out for delivery Order"
          : `${actionType.charAt(0).toUpperCase()}${actionType.slice(1)} Order`;
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.modalType = "status";
    modalRef.componentInstance.order = order;
    modalRef.componentInstance.actionType = actionType;
    modalRef.componentInstance.requiresReason = requiresReason;
    modalRef.componentInstance.setOutForDeliveryDefaults?.();

    modalRef.result
      .then((result) => {
        if (result) {
          if (actionType.toLowerCase() === "ready for pickup") {
            this.performReadyForPickUp(order._id);
          } else if (actionType.toLowerCase() === "out for delivery") {
            this.performOutForDelivery(order._id, result);
          } else {
            this.performAction(order._id, actionType, result);
          }
        }
      })
      .catch(() => {});
  }

  performReadyForPickUp(orderId: string): void {
    this.apiCallingService
      .callAPI(
        {},
        "POST",
        `${ConfigApi.URLS.VENDING.READY_FOR_PICKUP}/${orderId}`,
      )
      .subscribe(
        (res) => {
          if (this.apiCallingService.handleSuccess(res)) {
            this.toastr.success("Order marked as ready for pickup");
            this.getOrderDetails();
          }
        },
        (err) => {
          this.toastr.error("Failed to mark order as ready for pickup");
        },
      );
  }

  performOutForDelivery(
    orderId: string,
    payload: { riderMobile: string; message: string },
  ): void {
    this.apiCallingService
      .callAPI(
        { riderMobile: payload.riderMobile, message: payload.message },
        "POST",
        `${ConfigApi.URLS.VENDING.OUT_FOR_DELIVERY}/${orderId}`,
      )
      .subscribe(
        (res) => {
          if (this.apiCallingService.handleSuccess(res)) {
            this.toastr.success("Order marked as out for delivery");
            this.getOrderDetails();
          }
        },
        (err) => {
          this.toastr.error("Failed to mark order as out for delivery");
        },
      );
  }

  performAction(orderId: string, actionType: string, reason?: string): void {
    const statusMap: { [key: string]: string } = {
      accept: "accepted",
      "out for delivery": "outForDelivery",
      complete: "completed",
      cancel: "cancelled",
    };

    const status = statusMap[actionType.toLowerCase()];
    if (!status) return;

    const payload: any = { status };

    if (actionType.toLowerCase() === "cancel" && reason) {
      payload.cancel_reason = reason;
    }

    this.apiCallingService
      .callAPI(
        payload,
        "POST",
        `${ConfigApi.URLS.VENDING.UPDATE_ORDER}/${orderId}`,
      )
      .subscribe(
        (res) => {
          if (this.apiCallingService.handleSuccess(res)) {
            this.toastr.success(
              `Order ${actionType.toLowerCase()}ed successfully`,
            );
            this.getOrderDetails();
          }
        },
        (err) => {
          this.toastr.error(`Failed to ${actionType} order`);
        },
      );
  }

  getAvailableActions(): string[] {
    if (!this.orderDetails) {
      return [];
    }
    const actions = ["print"];

    if (
      this.orderDetails.deliveryType === "pickup" &&
      this.orderDetails.status?.toLowerCase() === "pending"
    ) {
      actions.push("readyForPickUp", "cancel");
    }

    if (
      this.orderDetails.deliveryType === "pickup" &&
      this.orderDetails.status === "readyToPickup"
    ) {
      actions.push("complete", "cancel");
    }

    if (this.orderDetails.deliveryType !== "delivery") {
      return actions;
    }

    switch (this.orderDetails.status?.toLowerCase()) {
      case "pending":
        actions.push("accept", "cancel");
        break;
      case "accepted":
        actions.push("outForDelivery", "cancel");
        break;
      case "outfordelivery":
        actions.push("complete", "cancel");
        break;
      default:
        break;
    }

    return actions;
  }

  getStatusBadgeClass(): string {
    if (!this.orderDetails.status) return "badge-secondary";

    switch (this.orderDetails.status.toLowerCase()) {
      case "pending":
        return "badge-warning";
      case "accepted":
        return "badge-info";
      case "out_for_delivery":
        return "badge-primary";
      case "readyToPickup":
        return "badge-info";
      case "completed":
        return "badge-success";
      case "cancelled":
      case "rejected":
        return "badge-danger";
      default:
        return "badge-secondary";
    }
  }

  getStatusDisplayText(): string {
    if (!this.orderDetails.status) return "Unknown";

    switch (this.orderDetails.status.toLowerCase()) {
      case "outfordelivery":
        return "Out for Delivery";
      case "readytopickup":
        return "Ready for Pickup";
      default:
        return (
          this.orderDetails.status.charAt(0).toUpperCase() +
          this.orderDetails.status.slice(1).toLowerCase()
        );
    }
  }
  hasMembershipDiscount(): boolean {
    return (
      this.orderDetails?.vendingPackagesItems?.some(
        (pkg: any) =>
          pkg.isFreeProductApplied ||
          pkg.membershipDiscountPercentage > 0 ||
          pkg.isFreeProductApplied,
      ) || false
    );
  }

  getDiscountCouponAmount(discount: number, amountAfterMembershipCut: number) {
    return (amountAfterMembershipCut * discount) / 100;
  }

  getItemLabel(pkg: any): string {
    const items = this.orderDetails?.vendingPackagesItems || [];
    const totalItems = items.length;
    const currency = this.orderDetails?.currency || "";

    const isFree = pkg.isFreeProductApplied === true;
    const hasDiscount =
      pkg.membershipDiscountPercentage > 0 && pkg.totalMembershipDiscount > 0;

    if (isFree && totalItems === 1) {
      return "Free";
    }

    if (isFree && hasDiscount) {
      return `Free + ${
        pkg.membershipDiscountPercentage
      }% (-${currency}${pkg.totalMembershipDiscount.toFixed(2)})`;
    }

    if (isFree) {
      return "Free";
    }

    if (hasDiscount) {
      return `${
        pkg.membershipDiscountPercentage
      }% (-${currency}${pkg.totalMembershipDiscount.toFixed(2)})`;
    }

    return "N/A";
  }

  PrintAddress(order: any) {
    const popup = window.open("", "_blank", "width=1200,height=800");

    if (!popup) {
      this.toastr.error("Popup blocked. Please allow popups for this site.");
      return;
    }

    const html = this.generatePrintHTML(order);

    setTimeout(() => {
      popup.document.open();
      popup.document.write(html);
      popup.document.close();
    }, 150);
  }

  generatePrintHTML(order: any) {
    const qr = order.deliveryAddressQr;
    const orderTrackingQr = order.orderTrackingQr || "";
    const productsHTML = (order?.vendingPackagesItems ?? [])
      .map((p: any) => {
        const isFree = p.isFreeProductApplied === true;
        const hasDiscount =
          p.membershipDiscountPercentage > 0 && p.totalMembershipDiscount > 0;
        let membershipDiscountText = "N/A";
        if (isFree && hasDiscount) {
          membershipDiscountText = `Free + ${
            p.membershipDiscountPercentage
          }% (-${order.currency}${p.totalMembershipDiscount.toFixed(2)})`;
        } else if (isFree) {
          membershipDiscountText = "Free";
        } else if (hasDiscount) {
          membershipDiscountText = `${p.membershipDiscountPercentage}% (-${
            order.currency
          }${p.totalMembershipDiscount.toFixed(2)})`;
        }

        return `
          <tr style="
      text-decoration: ${order.status === "refunded" ? "line-through" : "none"};
      text-decoration-color: ${order.status === "refunded" ? "red" : "initial"};
    ">
            <td>${p.packageId?.name}</td>
            <td><img src="${
              p.packageId?.productImage
            }" width="80" height="80" /></td>
          <td><b>${p.quantity}</b></td>
           <td><img src="${
             p.packageId?.barcodeImage
           }" width="80" height="80" /></td>
            <td>${p.packageId?.EANNumber || "N/A"}</td>
            <td>${order.currency} ${Number(p.packageId?.price ?? 0).toFixed(
              2,
            )}</td>
            <td>${membershipDiscountText}</td>
            <td>${Number(p.totalAmountWithoutVat ?? 0).toFixed(2)}</td>
            <td>${p.totalVatPercentage}%</td>
            <td>${Number(p.totalVatAmount ?? 0).toFixed(2)}</td>
            <td><b>${order.currency} ${Number(p.chargeableAmount ?? 0).toFixed(
              2,
            )}</b></td>
          </tr>
        `;
      })
      .join("");

    return `
    <html>
    <head>
      <title>${order.orderNumber}</title>
      <style>
        body { font-family: Arial; padding: 20px; }
        h2, h3 { margin: 0 0 12px; }
        .printTitleCus{
        margin: 12px 0;
        }
        .print-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
        }
        .print-title { flex: 1; }
        .qr-section {
          display: flex;
          gap: 16px;
          flex-direction: column;
        }
        .qr-card {
          padding: 12px;
          text-align: center;
          width: 150px;
        }
        .qr-card img {
          width: 120px;
          height: 120px;
          object-fit: contain;
          margin-top: 8px;
        }
        table { width:100%; border-collapse:collapse; }
        th, td { border:1px solid #000; padding:8px; }
        th { background:#eee; }
      </style>
    </head>
    <body onload="window.print(); window.close();">
      <div class="print-header">
        <div class="print-title">
          <h2>Order Details</h2>
          <h3>Order Information</h3>
          <div><b>Order Number:</b> ${order.orderNumber}</div>
          <div><b>Total Amount:</b> ${order.currency} ${(
            order.chargeableAmount / 100
          ).toFixed(2)}</div>
          <div><b>Status:</b> ${order.status}</div>
          <div><b>Delivery Type:</b> ${order.deliveryType}</div>
          <div><b>Payment Method:</b> ${order.paymentMethodType || "N/A"}</div>
          ${
            order.deliveryType === "delivery"
              ? `<div><b>Delivery Charge:</b> ${order.currency} ${order.deliveryPrice}</div>`
              : ""
          }
           ${
             order.status === "refunded"
               ? `<div><b>Partially Refund Amount:</b> ${order.currency} ${order.refundAmount}</div>`
               : ""
           }
          ${
            order.storeDeliveryServiceId
              ? `<div><b>Delivery Service:</b> ${order.storeDeliveryServiceId?.serviceName}</div>`
              : ""
          }
          ${
            order.deliveryServiceTrackingId
              ? `<div><b>Tracking ID:</b> ${order.deliveryServiceTrackingId}</div>`
              : ""
          }
          ${
            order.donationId
              ? `<div><b>Charity Name:</b> ${order.donationId?.charityName}</div>`
              : ""
          }
          ${
            order.donationTransactionId
              ? `<div><b>Donation Amount:</b> ${order.donationTransactionId?.amount} (${order.donationTransactionId?.donationPercentage}%)</div>`
              : ""
          }
          <div><b>Date:</b> ${new Date(order.created_at).toLocaleString()}</div>

          <h3 class="printTitleCus">Customer Information</h3>
          <div><b>Name:</b> ${order.user_id?.username}</div>
          <div><b>Email:</b> ${order.user_id?.email}</div>
          <div><b>Mobile:</b>${order.user_id?.dialling_code} ${
            order.user_id?.mobile
          }</div>
          ${
            order.deliveryType === "delivery"
              ? `<div><b>Address:</b> ${order.userAddress}</div>`
              : ""
          }
        </div>
        <div class="qr-section">
         <div class="qr-card">
                <b>Delivery QR Code</b>
                <img src="${qr}" alt="Delivery QR Code" />
              </div>
              
          ${
            orderTrackingQr
              ? `<div class="qr-card">
                <b>Order Tracking QR Code</b>
                <img src="${orderTrackingQr}" alt="Order Tracking QR Code" />
              </div>
              `
              : ""
          }
        </div>
      </div>
      <h3>Products</h3>
      <table>
        <thead>
          <tr>
            <th>Package Name</th>
            <th>Image</th>
            <th>Qty</th>
            <th>Barcode</th>
            <th>Barcode Image</th>
            <th>Unit Price</th>
            <th>Membership Discount</th>
            <th>Amount</th>
            <th>VAT %</th>
            <th>VAT Amt</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>${productsHTML}</tbody>
      </table>
    </body>
    </html>
  `;
  }

  editTrackingId() {
    this.trackingId = this.orderDetails.deliveryServiceTrackingId || "";
    this.showTrackingInput = true;
  }

  cancelTrackingId() {
    this.trackingId = "";
    this.showTrackingInput = false;
  }

  addTrackingId(orderId: string): void {
    if (!this.trackingId || !this.trackingId.trim()) {
      this.toastr.warning("Please enter a valid tracking ID");
      return;
    }

    const payload = {
      deliveryServiceTrackingId: this.trackingId.trim(),
    };

    this.apiCallingService
      .callAPI(
        payload,
        "POST",
        `${ConfigApi.URLS.VENDING.UPDATE_TRACKING_ID}/${orderId}`,
      )
      .subscribe(
        (res) => {
          if (this.apiCallingService.handleSuccess(res)) {
            this.toastr.success("Tracking ID added successfully");

            // Update local UI state
            this.orderDetails.deliveryServiceTrackingId = this.trackingId;
            this.trackingId = "";
            this.showTrackingInput = false;

            // Refresh order details (optional but safe)
            this.getOrderDetails();
          }
        },
        (err) => {
          this.toastr.error("Failed to add tracking ID");
        },
      );
  }
}
